'use client';
import { useTypedTranslation } from '@/hooks/useTypedTranslation';
import { useRef, useState, useEffect, KeyboardEvent, MouseEvent } from 'react';
import Image from 'next/image';
import styles from './ToolsSection.module.css';
import WeChatIcon from '@/components/ui/icons/WeChatIcon';

export default function ToolsSection() {
  const { t } = useTypedTranslation();
  const toolsSliderRef = useRef<HTMLDivElement>(null);
  const testimonialsSliderRef = useRef<HTMLDivElement>(null);

  // State for tracking drag operations
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);
  const [activeCardIndex, setActiveCardIndex] = useState(0);
  const [activeTestimonialIndex, setActiveTestimonialIndex] = useState(0);

  // Refs for card elements
  const toolCardRefs = useRef<Array<HTMLDivElement | null>>([]);
  const testimonialCardRefs = useRef<Array<HTMLDivElement | null>>([]);

  // Handle mouse down event for dragging
  const handleMouseDown = (e: MouseEvent<HTMLDivElement>) => {
    const target = e.currentTarget;

    setIsDragging(true);
    setStartX(e.pageX - target.offsetLeft);
    setScrollLeft(target.scrollLeft);
    target.style.cursor = 'grabbing';
  };

  // Handle mouse move event for dragging
  const handleMouseMove = (e: MouseEvent<HTMLDivElement>) => {
    if (!isDragging) return;

    const target = e.currentTarget;
    e.preventDefault();
    const x = e.pageX - target.offsetLeft;
    const walk = (x - startX) * 2; // Scroll speed multiplier
    target.scrollLeft = scrollLeft - walk;
  };

  // Handle mouse up event to end dragging
  const handleMouseUp = (e: MouseEvent<HTMLDivElement>) => {
    setIsDragging(false);
    e.currentTarget.style.cursor = 'grab';
  };

  // Handle mouse leave event
  const handleMouseLeave = (e: MouseEvent<HTMLDivElement>) => {
    if (isDragging) {
      setIsDragging(false);
      e.currentTarget.style.cursor = 'grab';
    }
  };

  // Handle keyboard navigation for tools slider
  const handleToolsKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'ArrowRight') {
      navigateToolsCards(1);
    } else if (e.key === 'ArrowLeft') {
      navigateToolsCards(-1);
    }
  };

  // Handle keyboard navigation for testimonials slider
  const handleTestimonialsKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'ArrowRight') {
      navigateTestimonialCards(1);
    } else if (e.key === 'ArrowLeft') {
      navigateTestimonialCards(-1);
    }
  };

  // Navigate tools cards
  const navigateToolsCards = (direction: number) => {
    const newIndex = Math.max(0, Math.min(2, activeCardIndex + direction));
    setActiveCardIndex(newIndex);
    toolCardRefs.current[newIndex]?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    toolCardRefs.current[newIndex]?.focus();
  };

  // Navigate testimonial cards
  const navigateTestimonialCards = (direction: number) => {
    const newIndex = Math.max(0, Math.min(3, activeTestimonialIndex + direction));
    setActiveTestimonialIndex(newIndex);
    testimonialCardRefs.current[newIndex]?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    testimonialCardRefs.current[newIndex]?.focus();
  };

  // Set initial cursor style
  useEffect(() => {
    if (toolsSliderRef.current) {
      toolsSliderRef.current.style.cursor = 'grab';
    }
    if (testimonialsSliderRef.current) {
      testimonialsSliderRef.current.style.cursor = 'grab';
    }
  }, []);

  return (
    <section className={styles.toolsSection}>
      <div className={styles.backgroundImage} />

      {/* Tools for Shoppers Subsection */}
      <div className={styles.subsection}>
        <div className={styles.container}>
          <div className={styles.subsectionHeader}>
            <h2 className={styles.title}>{t('toolsSection.title')}</h2>
            <p className={styles.subtitle}>{t('toolsSection.subtitle')}</p>
          </div>

          <div className={styles.sliderContainer}>
            <div
              className={styles.slider}
              ref={toolsSliderRef}
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseLeave}
              onKeyDown={handleToolsKeyDown}
              tabIndex={0}
              role="region"
              aria-label="Tools carousel"
            >
              {/* WeChat MiniProgram Card */}
              <div
                className={styles.card}
                ref={(el) => {
                  toolCardRefs.current[0] = el;
                }}
                tabIndex={0}
                role="button"
                aria-label="WeChat MiniProgram"
              >
                <div className={styles.cardContent}>
                  <div>
                    <h3 className={styles.cardTitle}>{t('toolsSection.wechat.title')}</h3>
                    <p className={styles.cardDescription}>{t('toolsSection.wechat.description')}</p>
                  </div>
                  <div className={styles.qrContainer}>
                    <Image
                      src="/images/QR_miniprogram_traveler_desktop.webp"
                      alt="WeChat MiniProgram"
                      width={178}
                      height={178}
                      className={styles.phoneImage}
                    />
                    <div className={styles.qrTextContainer}>
                      <p>Scan and go to wechat</p>
                      <WeChatIcon className={styles.icon} />
                    </div>
                  </div>
                </div>
                <div className={styles.phoneImageContainer}>
                  <Image
                    src="/images/tools_image_miniprogram_traveler_desktop.webp"
                    alt="WeChat MiniProgram"
                    width={268}
                    height={550}
                    className={styles.phoneImage}
                  />
                </div>
                <button className={styles.cardButton}>{t('toolsSection.wechat.button')}</button>
              </div>

              {/* LinePass Card */}
              <div
                className={styles.card}
                ref={(el) => {
                  toolCardRefs.current[1] = el;
                }}
                tabIndex={0}
                role="button"
                aria-label="LinePass App"
              >
                <div className={styles.cardContent}>
                  <div>
                    <h3 className={styles.cardTitle}>{t('toolsSection.linepass.title')}</h3>
                    <p className={styles.cardDescription}>
                      {t('toolsSection.linepass.description')}
                    </p>
                  </div>
                  <div className={styles.qrContainer}>
                    <Image
                      src="/images/QR_linepass_traveler_desktop.webp"
                      alt="LinePass App"
                      width={178}
                      height={178}
                      className={styles.phoneImage}
                    />
                    <div className={styles.qrTextContainer}>
                      <p>Scan to get your wallet pass</p>
                    </div>
                  </div>
                </div>
                <div className={styles.phoneImageContainer}>
                  <Image
                    src="/images/tools_image_linepass_traveler_desktop.webp"
                    alt="LinePass App"
                    width={268}
                    height={550}
                    className={styles.phoneImage}
                  />
                </div>
                <button className={styles.cardButton}>{t('toolsSection.linepass.button')}</button>
              </div>

              {/* Traveler App Card */}
              <div
                className={styles.card}
                ref={(el) => {
                  toolCardRefs.current[2] = el;
                }}
                tabIndex={0}
                role="button"
                aria-label="Traveler App"
              >
                <div className={styles.cardContent}>
                  <div>
                    <h3 className={styles.cardTitle}>{t('toolsSection.travelerApp.title')}</h3>
                    <p className={styles.cardDescription}>
                      {t('toolsSection.travelerApp.description')}
                    </p>
                  </div>
                  <div className={styles.qrContainer}>
                    <Image
                      src="/images/QR_costumerApp_traveler_desktop.webp"
                      alt="Traveler App"
                      width={178}
                      height={178}
                      className={styles.phoneImage}
                    />
                    <div className={styles.qrTextContainer}>
                      <p>Scan to download it</p>
                      <p>
                        <b>iOS & Android</b>
                      </p>
                    </div>
                  </div>
                </div>
                <div className={styles.phoneImageContainer}>
                  <Image
                    src="/images/tools_image_costumerapp_traveler_desktop.webp"
                    alt="Traveler App"
                    width={268}
                    height={550}
                    className={styles.phoneImage}
                  />
                </div>
                <div className={styles.appButtons}>
                  <button className={styles.appButton}>
                    <Image
                      src="/images/tools_button_apple_traveler_mobile.svg"
                      alt="Apple App"
                      width={120}
                      height={40}
                      // className={styles.icon}
                    />
                  </button>
                  <button className={styles.appButton}>
                    <Image
                      src="/images/tools_button_android_traveler_mobile.svg"
                      alt="Android App"
                      width={120}
                      height={40}
                      // className={styles.icon}
                    />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Testimonials Subsection */}
      <div className={styles.subsection}>
        <div className={styles.container}>
          <div className={styles.subsectionHeader}>
            <h2 className={styles.title}>{t('testimonials.title')}</h2>
          </div>

          <div className={styles.sliderContainer}>
            <div
              className={styles.slider}
              ref={testimonialsSliderRef}
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseLeave}
              onKeyDown={handleTestimonialsKeyDown}
              tabIndex={0}
              role="region"
              aria-label="Testimonials carousel"
            >
              {/* Testimonial 1 */}
              <div
                className={styles.testimonialCard}
                ref={(el) => {
                  testimonialCardRefs.current[0] = el;
                }}
                tabIndex={0}
                role="article"
                aria-label="Testimonial 1"
              >
                <h3 className={styles.testimonialName}>{t('testimonials.testimonial1.name')}</h3>
                <p className={styles.testimonialRole}>{t('testimonials.testimonial1.role')}</p>
                <p className={styles.testimonialText}>{t('testimonials.testimonial1.text')}</p>
              </div>

              {/* Testimonial 2 */}
              <div
                className={styles.testimonialCard}
                ref={(el) => {
                  testimonialCardRefs.current[1] = el;
                }}
                tabIndex={0}
                role="article"
                aria-label="Testimonial 2"
              >
                <h3 className={styles.testimonialName}>{t('testimonials.testimonial2.name')}</h3>
                <p className={styles.testimonialRole}>{t('testimonials.testimonial2.role')}</p>
                <p className={styles.testimonialText}>{t('testimonials.testimonial2.text')}</p>
              </div>

              {/* Testimonial 3 */}
              <div
                className={styles.testimonialCard}
                ref={(el) => {
                  testimonialCardRefs.current[2] = el;
                }}
                tabIndex={0}
                role="article"
                aria-label="Testimonial 3"
              >
                <h3 className={styles.testimonialName}>{t('testimonials.testimonial3.name')}</h3>
                <p className={styles.testimonialRole}>{t('testimonials.testimonial3.role')}</p>
                <p className={styles.testimonialText}>{t('testimonials.testimonial3.text')}</p>
              </div>

              {/* Testimonial 4 */}
              <div
                className={styles.testimonialCard}
                ref={(el) => {
                  testimonialCardRefs.current[3] = el;
                }}
                tabIndex={0}
                role="article"
                aria-label="Testimonial 4"
              >
                <h3 className={styles.testimonialName}>{t('testimonials.testimonial4.name')}</h3>
                <p className={styles.testimonialRole}>{t('testimonials.testimonial4.role')}</p>
                <p className={styles.testimonialText}>{t('testimonials.testimonial4.text')}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
